import React, { useEffect, useState, useRef } from "react";
import { db, auth, storage } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  orderBy,
  onSnapshot,
  increment,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useBuilding } from "../../context/BuildingContext";
import { cargarPersonalEdificio, claveId } from "../../utils/personalEdificio";
import {
  FaComments,
  FaPaperPlane,
  FaMicrophone,
  FaStop,
  FaPlay,
  FaPause,
  FaUser,
  FaCircle,
  FaSearch,
  FaVolumeUp,
} from "react-icons/fa";
import EmptyState from "../../components/EmptyState";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnGhost,
  inputBase,
  pageContainer,
  pageTitle,
} from "../../utils/designTokens";

const ChatAdmin = () => {
  const { selectedBuilding } = useBuilding();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [playingAudio, setPlayingAudio] = useState(null);

  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const unsubMessagesRef = useRef(null);

  const buildingId = selectedBuilding?.buildingid;
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!buildingId) return;
    fetchEmployees();
  }, [buildingId]);

  useEffect(() => {
    if (!buildingId) return;
    const q = query(
      collection(db, "employee_chats"),
      where("buildingid", "==", buildingId),
      orderBy("lastMessageAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const chatList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setChats(chatList);
      },
      (error) => {
        console.error("ChatAdmin chats onSnapshot error (may need index):", error);
        // Fallback: cargar sin orderBy
        const fallback = async () => {
          try {
            const snap = await getDocs(
              query(collection(db, "employee_chats"), where("buildingid", "==", buildingId))
            );
            const chatList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            chatList.sort((a, b) => (b.lastMessageAt?.toMillis?.() || 0) - (a.lastMessageAt?.toMillis?.() || 0));
            setChats(chatList);
          } catch (e) {
            console.error("Fallback chats load failed:", e);
          }
        };
        fallback();
      }
    );
    return () => unsub();
  }, [buildingId]);

  useEffect(() => {
    if (!selectedChat) {
      setMessages([]);
      return;
    }
    if (unsubMessagesRef.current) unsubMessagesRef.current();

    const chatId = selectedChat.id;
    const q = query(
      collection(db, "employee_chat_messages"),
      where("chatId", "==", chatId),
      orderBy("timestamp", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMessages(msgs);
        markAsRead(chatId);
        setTimeout(() => scrollToBottom(), 100);
      },
      (error) => {
        console.error("ChatAdmin messages onSnapshot error (may need index):", error);
        // Fallback: cargar sin orderBy, ordenar en el cliente
        const fallback = async () => {
          try {
            const snap = await getDocs(
              query(collection(db, "employee_chat_messages"), where("chatId", "==", chatId))
            );
            const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            msgs.sort((a, b) => (a.timestamp?.toMillis?.() || 0) - (b.timestamp?.toMillis?.() || 0));
            setMessages(msgs);
            markAsRead(chatId);
          } catch (e) {
            console.error("Fallback messages load failed:", e);
          }
        };
        fallback();
      }
    );
    unsubMessagesRef.current = unsub;
    return () => unsub();
  }, [selectedChat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Personal del edificio: employeebuilding por `building` + nombre de la
  // ficha users (utils/personalEdificio). Sin escanear la colección employee.
  const fetchEmployees = async () => {
    try {
      const personal = await cargarPersonalEdificio(buildingId);
      const empMap = {};
      personal.forEach((p) => {
        empMap[claveId(p.employeeid)] = p;
      });
      setEmployees(empMap);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees({});
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const markAsRead = async (chatId) => {
    try {
      // Marcar mensajes no leídos como leídos
      const q = query(
        collection(db, "employee_chat_messages"),
        where("chatId", "==", chatId),
        where("read", "==", false)
      );
      const snap = await getDocs(q);
      const updates = snap.docs
        .filter((d) => d.data().senderId !== currentUser?.uid)
        .map((d) => updateDoc(doc(db, "employee_chat_messages", d.id), { read: true }));
      await Promise.all(updates);

      // Resetear contador de no leídos en el chat
      await updateDoc(doc(db, "employee_chats", chatId), {
        unreadCountAdmin: 0,
      });
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    const text = newMessage.trim();
    setNewMessage("");

    // Actualización optimista — mostrar mensaje inmediatamente
    const now = Timestamp.now();
    const optimisticMsg = {
      id: `optimistic_${Date.now()}`,
      chatId: selectedChat.id,
      senderId: currentUser?.uid || "admin",
      senderName: currentUser?.displayName || currentUser?.email || "Admin",
      message: text,
      timestamp: now,
      read: false,
      type: "text",
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    setTimeout(() => scrollToBottom(), 50);

    try {
      await addDoc(collection(db, "employee_chat_messages"), {
        chatId: selectedChat.id,
        senderId: currentUser?.uid || "admin",
        senderName: currentUser?.displayName || currentUser?.email || "Admin",
        message: text,
        timestamp: now,
        read: false,
        type: "text",
      });
      await updateDoc(doc(db, "employee_chats", selectedChat.id), {
        lastMessage: text,
        lastMessageAt: Timestamp.now(),
        unreadCountEmployee: increment(1),
      });
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        // SEC: Validar tamaño antes de subir (10MB max)
        if (blob.size > 10 * 1024 * 1024) {
          console.warn("Audio demasiado grande:", blob.size);
          return;
        }
        await sendAudioMessage(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error starting recording:", err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(recordingIntervalRef.current);
  };

  const sendAudioMessage = async (blob) => {
    if (!selectedChat) return;
    try {
      const fileName = `chat_audio/${selectedChat.id}/${Date.now()}.webm`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, blob);
      const audioUrl = await getDownloadURL(storageRef);

      await addDoc(collection(db, "employee_chat_messages"), {
        chatId: selectedChat.id,
        senderId: currentUser?.uid || "admin",
        senderName: currentUser?.displayName || currentUser?.email || "Admin",
        message: "",
        timestamp: Timestamp.now(),
        read: false,
        type: "audio",
        audioUrl,
        audioTranscription: "",
        audioDuration: recordingTime,
      });
      await updateDoc(doc(db, "employee_chats", selectedChat.id), {
        lastMessage: "Mensaje de audio",
        lastMessageAt: Timestamp.now(),
        unreadCountEmployee: increment(1),
      });
    } catch (err) {
      console.error("Error sending audio:", err);
    }
  };

  const playAudio = (url, msgId) => {
    if (playingAudio === msgId) {
      audioRef.current?.pause();
      setPlayingAudio(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.play();
    setPlayingAudio(msgId);
    audio.onended = () => setPlayingAudio(null);
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const getEmployeeName = (chat) => {
    // El chat guarda employeeid (ChatEmployee); participants es de datos viejos.
    const empId =
      chat.employeeid ??
      chat.participants?.find((p) => p !== currentUser?.uid && p !== "admin");
    const emp = empId != null ? employees[claveId(empId)] : null;
    if (emp?.name) return emp.name;
    return chat.employeeName || "Empleado";
  };

  const filteredChats = chats.filter((c) => {
    if (!searchTerm) return true;
    const name = getEmployeeName(c).toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const formatRecordingTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Sin edificio seleccionado
  if (!buildingId) {
    return (
      <div style={styles.container}>
        <EmptyState
          icon={FaComments}
          title="Seleccioná un edificio para ver los chats"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaComments style={{ marginRight: 10, color: colors.primary }} />
          Chat con Empleados
        </h1>
      </div>

      <div style={styles.chatContainer}>
        {/* Panel izquierdo - Lista de conversaciones */}
        <div style={styles.leftPanel}>
          <div style={styles.searchBox}>
            <FaSearch style={{ color: colors.gray400, marginRight: spacing[2] }} />
            <input
              type="text"
              placeholder="Buscar empleado..."
              style={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={styles.chatList}>
            {filteredChats.length === 0 ? (
              <div style={{ padding: spacing[6], textAlign: "center", color: colors.gray400 }}>
                No hay conversaciones
              </div>
            ) : (
              filteredChats.map((chat) => (
                <div
                  key={chat.id}
                  style={{
                    ...styles.chatItem,
                    ...(selectedChat?.id === chat.id ? styles.chatItemActive : {}),
                  }}
                  onClick={() => setSelectedChat(chat)}
                >
                  <div style={styles.chatAvatar}>
                    <FaUser style={{ color: colors.white, fontSize: typography.sizes.lg }} />
                  </div>
                  <div style={styles.chatInfo}>
                    <div style={styles.chatName}>{getEmployeeName(chat)}</div>
                    <div style={styles.chatPreview}>
                      {chat.lastMessage || "Sin mensajes"}
                    </div>
                  </div>
                  <div style={styles.chatMeta}>
                    <span style={styles.chatTime}>
                      {formatDate(chat.lastMessageAt)}
                    </span>
                    {(chat.unreadCountAdmin || 0) > 0 && (
                      <span style={styles.unreadBadge}>
                        {chat.unreadCountAdmin}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Panel derecho - Mensajes */}
        <div style={styles.rightPanel}>
          {!selectedChat ? (
            <div style={styles.noChat}>
              <FaComments style={{ fontSize: 56, color: colors.gray200, marginBottom: spacing[4] }} />
              <p style={{ color: colors.gray400, fontSize: typography.sizes.lg }}>
                Seleccioná una conversación para ver los mensajes
              </p>
            </div>
          ) : (
            <>
              <div style={styles.chatHeader}>
                <div style={styles.chatAvatar}>
                  <FaUser style={{ color: colors.white, fontSize: typography.sizes.lg }} />
                </div>
                <span style={{ fontWeight: typography.weights.normal, fontSize: typography.sizes.lg }}>
                  {getEmployeeName(selectedChat)}
                </span>
              </div>

              <div style={styles.messagesContainer}>
                {messages.map((msg) => {
                  const isAdmin =
                    msg.senderId === currentUser?.uid || msg.senderId === "admin";
                  return (
                    <div
                      key={msg.id}
                      style={{
                        ...styles.messageBubbleRow,
                        justifyContent: isAdmin ? "flex-end" : "flex-start",
                      }}
                    >
                      <div
                        style={{
                          ...styles.messageBubble,
                          ...(isAdmin ? styles.bubbleAdmin : styles.bubbleEmployee),
                        }}
                      >
                        {msg.type === "audio" ? (
                          <div style={styles.audioBubble}>
                            <button
                              style={styles.audioPlayBtn}
                              onClick={() => playAudio(msg.audioUrl, msg.id)}
                              aria-label={playingAudio === msg.id ? "Pausar audio" : "Reproducir audio"}
                            >
                              {playingAudio === msg.id ? (
                                <FaPause />
                              ) : (
                                <FaPlay />
                              )}
                            </button>
                            <div style={styles.audioWave}>
                              <FaVolumeUp style={{ color: isAdmin ? colors.white : colors.primary }} />
                              <span
                                style={{
                                  fontSize: typography.sizes.sm,
                                  color: isAdmin ? "rgba(255,255,255,0.8)" : colors.gray600,
                                  marginLeft: 6,
                                }}
                              >
                                {msg.audioDuration
                                  ? formatRecordingTime(msg.audioDuration)
                                  : "0:00"}
                              </span>
                            </div>
                            {msg.audioTranscription && (
                              <p
                                style={{
                                  fontSize: typography.sizes.sm,
                                  fontStyle: "italic",
                                  margin: "6px 0 0",
                                  color: isAdmin
                                    ? "rgba(255,255,255,0.7)"
                                    : colors.gray600,
                                }}
                              >
                                {msg.audioTranscription}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p style={styles.messageText}>{msg.message}</p>
                        )}
                        <span
                          style={{
                            ...styles.messageTime,
                            color: isAdmin
                              ? "rgba(255,255,255,0.7)"
                              : colors.gray400,
                          }}
                        >
                          {formatTime(msg.timestamp)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div style={styles.inputBar}>
                {isRecording ? (
                  <div style={styles.recordingBar}>
                    <FaCircle
                      style={{
                        color: colors.error,
                        fontSize: 10,
                        animation: "pulse 1s infinite",
                      }}
                    />
                    <span style={{ color: colors.error, fontWeight: typography.weights.normal, marginLeft: spacing[2] }}>
                      {formatRecordingTime(recordingTime)}
                    </span>
                    <button style={styles.stopRecordBtn} onClick={stopRecording} aria-label="Detener grabación">
                      <FaStop />
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Escribí un mensaje..."
                      style={styles.messageInput}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    />
                    <button style={styles.micBtn} onClick={startRecording} aria-label="Grabar audio">
                      <FaMicrophone />
                    </button>
                    <button
                      style={{
                        ...styles.sendBtn,
                        opacity: newMessage.trim() ? 1 : 0.5,
                      }}
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      aria-label="Enviar mensaje"
                    >
                      <FaPaperPlane />
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Estilos ──────────────────────────────────────────────────────────────────

const styles = {
  // Contenedor principal de la página
  container: {
    ...pageContainer,
  },
  header: {
    marginBottom: spacing[5],
  },
  // Título de la pantalla
  title: {
    ...pageTitle,
    display: "flex",
    alignItems: "center",
  },
  // Contenedor del chat (panel izquierdo + derecho)
  chatContainer: {
    display: "flex",
    height: "calc(100vh - 140px)",
    background: colors.bgCard,
    borderRadius: radius.md,
    boxShadow: shadows.md,
    overflow: "hidden",
  },
  // Panel izquierdo - lista de conversaciones
  leftPanel: {
    width: "30%",
    borderRight: `1px solid ${colors.gray200}`,
    display: "flex",
    flexDirection: "column",
  },
  // Barra de búsqueda de empleados
  searchBox: {
    display: "flex",
    alignItems: "center",
    padding: `${spacing[3]}px ${spacing[4]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: typography.sizes.md,
    flex: 1,
    fontFamily: typography.fontFamily,
  },
  // Lista de chats
  chatList: {
    flex: 1,
    overflowY: "auto",
  },
  // Cada item de chat en la lista
  chatItem: {
    display: "flex",
    alignItems: "center",
    padding: `14px ${spacing[4]}px`,
    cursor: "pointer",
    borderBottom: `1px solid ${colors.gray100}`,
    transition: `background ${transitions.fast}`,
  },
  // Item activo (seleccionado)
  chatItemActive: {
    background: colors.primaryLighter,
    borderLeft: `3px solid ${colors.primary}`,
  },
  // Avatar circular del empleado
  chatAvatar: {
    width: spacing[10],
    height: spacing[10],
    borderRadius: radius.full,
    background: colors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: spacing[3],
    flexShrink: 0,
  },
  chatInfo: {
    flex: 1,
    overflow: "hidden",
  },
  // Nombre del empleado en la lista
  chatName: {
    fontWeight: typography.weights.normal,
    fontSize: typography.sizes.md,
    color: colors.black,
    marginBottom: 2,
  },
  // Preview del último mensaje
  chatPreview: {
    fontSize: typography.sizes.base,
    color: colors.gray400,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chatMeta: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
    marginLeft: spacing[2],
  },
  // Hora del último mensaje
  chatTime: {
    fontSize: typography.sizes.xs,
    color: colors.gray300,
  },
  // Badge de mensajes no leídos
  unreadBadge: {
    background: colors.primary,
    color: colors.white,
    fontSize: typography.sizes.xs,
    fontWeight: typography.weights.normal,
    borderRadius: radius.md,
    padding: `2px ${spacing[2]}px`,
    minWidth: 20,
    textAlign: "center",
  },
  // Panel derecho - mensajes
  rightPanel: {
    width: "70%",
    display: "flex",
    flexDirection: "column",
  },
  // Estado vacío (sin chat seleccionado)
  noChat: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  // Cabecera del chat activo
  chatHeader: {
    display: "flex",
    alignItems: "center",
    padding: `14px ${spacing[5]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
    background: colors.gray50,
  },
  // Contenedor de mensajes
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: spacing[5],
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
  },
  // Fila de burbuja de mensaje
  messageBubbleRow: {
    display: "flex",
  },
  // Burbuja de mensaje base
  messageBubble: {
    maxWidth: "65%",
    padding: `10px 14px`,
    borderRadius: radius.lg,
  },
  // Burbuja del admin (azul)
  bubbleAdmin: {
    background: colors.primary,
    color: colors.white,
    borderBottomRightRadius: 4,
  },
  // Burbuja del empleado (gris claro)
  bubbleEmployee: {
    background: colors.gray100,
    color: colors.gray900,
    borderBottomLeftRadius: 4,
  },
  // Texto del mensaje
  messageText: {
    margin: 0,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
  // Hora del mensaje
  messageTime: {
    fontSize: typography.sizes.xs,
    display: "block",
    marginTop: 4,
  },
  // Burbuja de audio
  audioBubble: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    flexWrap: "wrap",
  },
  // Botón de reproducir audio
  audioPlayBtn: {
    width: 32,
    height: 32,
    borderRadius: radius.full,
    border: "none",
    background: "rgba(255,255,255,0.2)",
    color: colors.white,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: typography.sizes.sm,
  },
  audioWave: {
    display: "flex",
    alignItems: "center",
  },
  // Barra de input de mensaje
  inputBar: {
    display: "flex",
    alignItems: "center",
    padding: `${spacing[3]}px ${spacing[4]}px`,
    borderTop: `1px solid ${colors.gray200}`,
    gap: spacing[2],
  },
  // Input de texto del mensaje
  messageInput: {
    flex: 1,
    padding: `10px ${spacing[4]}px`,
    borderRadius: radius.full,
    border: `1px solid ${colors.gray200}`,
    fontSize: typography.sizes.md,
    outline: "none",
    fontFamily: typography.fontFamily,
  },
  // Botón de enviar mensaje
  sendBtn: {
    width: spacing[10],
    height: spacing[10],
    borderRadius: radius.full,
    border: "none",
    background: colors.primary,
    color: colors.white,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    transition: `opacity ${transitions.fast}`,
  },
  // Botón de micrófono
  micBtn: {
    width: spacing[10],
    height: spacing[10],
    borderRadius: radius.full,
    border: "none",
    background: colors.gray100,
    color: colors.gray700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    transition: `background ${transitions.fast}`,
  },
  // Barra de grabación de audio
  recordingBar: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    padding: `0 ${spacing[4]}px`,
  },
  // Botón de detener grabación
  stopRecordBtn: {
    width: spacing[10],
    height: spacing[10],
    borderRadius: radius.full,
    border: "none",
    background: colors.error,
    color: colors.white,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: typography.sizes.md,
    marginLeft: "auto",
    transition: `background ${transitions.fast}`,
  },
};

export default ChatAdmin;
